import { definitions } from '@luxuryescapes/contract-trip'
import { useInfiniteQuery, UseInfiniteQueryResult, useMutation, UseMutationOptions, UseMutationResult, useQuery, useQueryClient, UseQueryOptions, UseQueryResult } from '@tanstack/react-query'
import { useAppSelector } from 'hooks/reduxHooks'
import { EmptyObject } from 'lib/object/objectUtils'
import { CuratedTripFilters, getCuratedTrips, getCuratedTripsWithPagination } from 'tripPlanner/api/templates'
import * as Mapper from 'tripPlanner/mappers'
import * as CuratedTripKeys from '../reactQueryKeys/curatedTrips'
import { cancelAndInvalidate, invalidateTrip, TRIP_STALE_TIME } from './common'
import { BasicTrip, Curator, FullTrip, TripMetadata, TripSummary } from 'tripPlanner/types/common'
import { useCallback, useMemo } from 'react'
import { CopyTemplateError, GetCuratedTripsResponse, GetTripCuratorError, GetTripError, PublishTripError, SubmitForApprovalError, UnpublishTripError } from 'tripPlanner/api/types'
import { copyTripTemplate, fetchCurator, getCuratedTrip, getCuratedTripMetadata, getCuratedTripSummary, publishTripTemplate, submitTripForApproval, unpublishTripTemplate, updateReviewData, validateTemplate } from 'tripPlanner/api'
import * as TripKeys from '../reactQueryKeys/trips'

export interface UseCuratedTripsOptions
  extends UseQueryOptions<
    Array<definitions['basicTrip']>,
    unknown,
    Array<BasicTrip>
  > {
  filters?: CuratedTripFilters
  limit?: number
}

export const TRIP_API_DEFAULT_CURATED_TRIPS: Array<definitions['basicTrip']> = []

export const useCuratedTrips = (
  options: UseCuratedTripsOptions = EmptyObject,
) => {
  const { filters = EmptyObject, limit, ...queryOptions } = options
  const regionCode = useAppSelector((state) => state.geo.currentRegionCode)
  const timezoneOffset = new Date().getTimezoneOffset() / 60
  return useQuery({
    queryKey: CuratedTripKeys.lists(regionCode, timezoneOffset, filters, limit),
    queryFn: () => getCuratedTrips(regionCode, timezoneOffset, filters, limit),
    select: Mapper.basicTrips,
    initialData: undefined,
    placeholderData: TRIP_API_DEFAULT_CURATED_TRIPS,
    staleTime: TRIP_STALE_TIME,
    ...queryOptions,
  })
}

export const usePaginatedCuratedTrips = () => {
  const regionCode = useAppSelector((state) => state.geo.currentRegionCode)
  const timezoneOffset = new Date().getTimezoneOffset() / 60
  const fetchCuratedTrips = async({ pageParam = 0 }) => await getCuratedTripsWithPagination(pageParam, regionCode, timezoneOffset)

  const {
    data, fetchNextPage, hasNextPage, isFetchingNextPage, isSuccess, ...otherOptions
  }: UseInfiniteQueryResult<GetCuratedTripsResponse, null> = useInfiniteQuery({
    queryKey: CuratedTripKeys.paginatedTemplates,
    queryFn: fetchCuratedTrips,
    initialData: undefined,
    staleTime: TRIP_STALE_TIME,
    getNextPageParam: (lastPage) => {
      if (lastPage?.hasMore) {
        return lastPage?.offset
      }
      return undefined
    },
  })

  const trips = useMemo(() => {
    if (isSuccess) {
      const pages = data?.pages ?? []
      return pages.flatMap((page) => {
        const results = page?.result
        return results?.map(Mapper.basicTrip)
      })
    }
    return []
  }, [isSuccess, data])

  return {
    data: trips,
    hasMore: data?.pages[data.pages?.length - 1].hasMore,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    ...otherOptions,
  }
}

export const useCuratedTrip = ({
  tripId, ...options
}: {
  tripId: string
} & UseQueryOptions<
  definitions['fullTrip'], GetTripError, FullTrip, ReturnType<typeof CuratedTripKeys.detail>
>): UseQueryResult<FullTrip, GetTripError> => {
  return useQuery({
    queryKey: CuratedTripKeys.detail(tripId),
    queryFn: () => getCuratedTrip(tripId),
    select: Mapper.fullTrip,
    ...options,
  })
}

export const useCuratedTripSummary = ({
  tripId,
}: {
  tripId: string
}): UseQueryResult<TripSummary, GetTripError> => {
  return useQuery(
    CuratedTripKeys.summary(tripId),
    () => getCuratedTripSummary(tripId),
    {
      select: Mapper.tripSummary,
    },
  )
}

export const useCuratedTripMetadata = ({
  tripId,
}: {
  tripId: string
}): UseQueryResult<TripMetadata, GetTripError> => {
  return useQuery(
    CuratedTripKeys.metadata(tripId),
    () => getCuratedTripMetadata(tripId),
    {
      select: Mapper.tripMetadata,
    },
  )
}

type ReviewArgs = {
  userId: string
  name: string
  position?: string
  imageId?: string
  description?: string
}

export const usePublish = ({
  tripId, options,
}: {
  tripId: string
  options?: UseMutationOptions<null, PublishTripError>
}) => {
  const queryClient = useQueryClient()
  const onSuccess = () => {
    invalidateTrip(queryClient, tripId)
  }

  return useMutation({
    mutationFn: () => publishTripTemplate({ tripId }),
    onSuccess,
    ...options,
  })
}

export const useUnpublish = ({
  tripId, options,
}: {
  tripId: string
  options?: UseMutationOptions<null, UnpublishTripError>
}) => {
  const queryClient = useQueryClient()
  const onSuccess = () => {
    invalidateTrip(queryClient, tripId)
  }

  return useMutation({
    mutationFn: () => unpublishTripTemplate({ tripId }),
    onSuccess,
    ...options,
  })
}

export const useSubmitForApproval = ({
  tripId, options,
}: {
  tripId: string
  options?: UseMutationOptions<null, SubmitForApprovalError, ReviewArgs>
}) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ userId, ...formData }) => submitTripForApproval({
      tripId,
      userId,
      ...formData,
    }),
    ...options,
    onSuccess: (data, vars, context) => {
      cancelAndInvalidate(queryClient, TripKeys.collaborator(tripId))
      cancelAndInvalidate(queryClient, TripKeys.detail(tripId))
      options?.onSuccess?.(data, vars, context)
    },
  })
}

type UpdateReviewArgs = {
  name?: string
  position?: string
  imageId?: string
  description?: string
}

export const useUpdateReview = ({
  tripId, options,
}: {
  tripId: string
  options?: UseMutationOptions<null, SubmitForApprovalError, UpdateReviewArgs>
}) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (formData) => updateReviewData({ tripId, ...formData }),
    ...options,
    onSuccess: (data, vars, context) => {
      cancelAndInvalidate(queryClient, TripKeys.detail(tripId))
      options?.onSuccess?.(data, vars, context)
    },
  })
}

export const useUpdateAndPublish = ({
  tripId, options,
}: {
  tripId: string
  options?: UseMutationOptions<null, PublishTripError>
}) => {
  const { mutate: publishTripMutate } = usePublish({ tripId, options })

  const onSuccess = useCallback(() => {
    publishTripMutate()
  }, [publishTripMutate])

  return useUpdateReview({ tripId, options: { onSuccess } })
}

export const useCopyTripTemplate = (
  options?: UseMutationOptions<
    FullTrip, CopyTemplateError, Parameters<typeof copyTripTemplate>[0]
  >,
): UseMutationResult<
  FullTrip, CopyTemplateError, Parameters<typeof copyTripTemplate>[0]
> => {
  const queryClient = useQueryClient()

  return useMutation(
    (params) => copyTripTemplate(params).then(Mapper.fullTrip),
    {
      retry: false,
      ...options,
      onSettled: (trip, error, variables, context) => {
        cancelAndInvalidate(queryClient, TripKeys.lists)
        options?.onSettled?.(trip, error, variables, context)
      },
    },
  )
}

export const useCurator = ({
  tripId,
}: {
  tripId: string
  onSuccess?: (data: Curator) => void
  onError?: (err: GetTripCuratorError) => void
}): UseQueryResult<Curator, GetTripCuratorError> => {
  return useQuery({
    queryKey: CuratedTripKeys.curator(tripId),
    queryFn: () => fetchCurator({ tripId }),
  })
}

export const selectMessages = (
  items: Array<definitions['validationError']>,
): Array<string> => {
  return items.map((item) => item.message)
}
export const useTemplateValidation = ({ tripId }: { tripId: string}) => {
  const { data = [] } = useQuery({
    queryKey: CuratedTripKeys.validation(tripId),
    queryFn: () => validateTemplate({ tripId }),
    select: selectMessages,
  })
  return { data, hasValidationErrors: data.length > 0 }
}
